'use client';

import {
  AccordionGroup,
  AccordionItem,
  Button,
  Dropdown,
  DropdownItem,
  IconButton,
  LinkButton,
} from '@ads/components-react';
import { ChevronLeft, ChevronRight } from '@ads/icons';
import { cn } from '@linka/utils';
import { Fragment, type ReactNode, useEffect, useReducer, useRef, useState } from 'react';

export interface SidebarLayoutItem {
  id: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  children?: SidebarLayoutItem[];
}

export interface SidebarLayoutSection {
  id: string;
  heading?: ReactNode;
  items: SidebarLayoutItem[];
}

interface SidebarLayoutRenderLinkProps {
  className?: string;
  children: ReactNode;
}

interface SidebarLayoutRenderLogoLinkProps {
  href: string;
  'aria-label': string;
  children: ReactNode;
}

export interface SidebarLayoutProps {
  sections: SidebarLayoutSection[];
  renderLink?: (item: SidebarLayoutItem, props: SidebarLayoutRenderLinkProps) => ReactNode;
  renderLogoLink?: (props: SidebarLayoutRenderLogoLinkProps) => ReactNode;
  expandLabel?: string;
  collapseLabel?: string;
  defaultExpanded?: boolean;
  collapseSignal?: unknown;
  onExpandedChange?: (expanded: boolean) => void;
  navAriaLabel?: string;
  sidebarContent?: ReactNode;
  sidebarFooter?: ReactNode;
  header?: ReactNode;
  logoHref?: string;
  fullBleed?: boolean;
  children: ReactNode;
}

const defaultRenderLogoLink = ({ children, ...props }: SidebarLayoutRenderLogoLinkProps) => (
  <a {...props}>{children}</a>
);

const SidebarLayout = ({
  sections,
  renderLink,
  renderLogoLink,
  expandLabel = 'Abrir menu lateral',
  collapseLabel = 'Fechar menu lateral',
  defaultExpanded = false,
  collapseSignal,
  onExpandedChange,
  navAriaLabel,
  sidebarContent,
  sidebarFooter,
  header,
  logoHref = '/',
  fullBleed = false,
  children,
}: SidebarLayoutProps) => {
  const [expanded, dispatch] = useReducer(
    (state: boolean, action: 'toggle' | 'collapse') => (action === 'toggle' ? !state : false),
    defaultExpanded,
  );
  const isFirstRender = useRef(true);
  const toggleExpanded = () => dispatch('toggle');

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch('collapse');
  }, [collapseSignal]);

  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  const allItems = sections.flatMap((section) => section.items);
  const activeGroupIds = findActiveGroupIds(allItems);
  const [openGroups, setOpenGroups] = useState<string[]>(activeGroupIds);

  const activeGroupIdsKey = activeGroupIds.join(',');

  useEffect(() => {
    setOpenGroups((current) => Array.from(new Set([...current, ...activeGroupIds])));
  }, [activeGroupIdsKey]);

  return (
    <div className='flex min-h-screen'>
      <aside
        data-expanded={expanded}
        className={cn(
          'group fixed inset-0 h-screen md:max-w-[17.5rem] z-50 transition-[width,left] ease-in-out',
          expanded ? 'left-0 w-2/3 md:w-[17.5rem]' : '-left-full w-xxl md:left-0',
        )}
      >
        <IconButton
          ariaLabel={expanded ? collapseLabel : expandLabel}
          onClick={toggleExpanded}
          hierarchy='secondary'
          icon={expanded ? ChevronLeft : ChevronRight}
          className={cn('!bg-base-neutral-white absolute top-xxxs shadow-level3 -right-xxs z-40 md:flex', {
            hidden: !expanded,
            flex: expanded,
          })}
        />
        <div className='flex flex-col bg-ctx-layout-surface px-mini h-full border-r border-ctx-layout-border'>
          <section className='flex items-center justify-center gap-mini w-full min-h-[5rem]'>
            {(renderLogoLink ?? defaultRenderLogoLink)({
              href: logoHref,
              'aria-label': 'Linka Eventos',
              children: (
                <>
                  <img
                    src='/svg/logo-linka-xxl-vertical.svg'
                    alt='Linka Eventos'
                    className='hidden group-data-[expanded=false]:block h-lg'
                  />
                  <img
                    src='/svg/logo-linka-xxl-horizontal.svg'
                    alt='Linka Eventos'
                    className='hidden group-data-[expanded=true]:block h-sm'
                  />
                </>
              ),
            })}
          </section>
          <section className='grow flex flex-col gap-xs min-h-0'>
            {sidebarContent}
            <nav
              aria-label={navAriaLabel}
              className={cn('min-h-0 overflow-y-auto overflow-x-hidden', scrollbarClassName)}
            >
              <AccordionGroup type='multiple' value={openGroups} onValueChange={setOpenGroups}>
                <ul className='flex flex-col gap-mini text-ctx-content-base'>
                  {sections.map((section, index) => (
                    <Fragment key={section.id}>
                      {index > 0 && <div aria-hidden className='w-full border-t border-base-gray-100' />}
                      {section.heading && (
                        <p className='ts-caption-xxs text-ctx-content-base/semiOpaque px-nano'>{section.heading}</p>
                      )}
                      {section.items.map((item) => (
                        <li
                          key={item.id}
                          className={cn('flex items-center justify-start rounded-pill', {
                            'hover:bg-[#D6E4FF7A]': !item.children?.length,
                          })}
                        >
                          {item.children?.length ? (
                            <MenuGroup item={item} renderLink={renderLink} />
                          ) : (
                            <MenuLink item={item} renderLink={renderLink} />
                          )}
                        </li>
                      ))}
                    </Fragment>
                  ))}
                </ul>
              </AccordionGroup>
            </nav>
          </section>
          {sidebarFooter && <footer className='border-t border-base-gray-100 pb-sm pt-xs'>{sidebarFooter}</footer>}
        </div>
      </aside>
      <div className='w-full'>
        <header className='sticky top-0 z-30 min-h-xxl gap-xs bg-ctx-layout-surface px-xs w-full flex items-center justify-between md:justify-end'>
          <IconButton
            ariaLabel={expandLabel}
            onClick={toggleExpanded}
            hierarchy='secondary'
            icon={ChevronRight}
            className={cn('flex md:!hidden')}
          />
          {header}
        </header>
        <main
          className={cn(
            'overflow-y-auto',
            fullBleed ? 'md:pl-[5rem] lg:h-[calc(100vh-5rem)]' : 'p-xxxs md:p-xs md:pl-[7rem]',
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

// NOTE Imita o ScrollArea do ADS com a barra nativa. Os `::-webkit-scrollbar-*` valem para Chromium/Safari; o
// Firefox cai no `scrollbar-width`/`scrollbar-color`, aplicados só onde `::-webkit-scrollbar` não existe,
// porque o Chrome ignora os pseudo-elementos quando essas propriedades estão definidas.
// Porquê foi feito assim? Hoje o ScrollArea do Arandu é um overlay sobre o conteudo que exige ajuste fino para
// evitar que o thumb da barra de rolagem mesmo quando invisível capture eventos de clique na sua região as vezes
// causando bugs indesejados ao tentar clicar em um Button dentro da ScollArea. Este workaround imita o estilo da
// barra de rolagem do Arandu usando a barra de rolagem nativa do UserAgent garantindo a usabilidade.
const scrollbarClassName = [
  '[&::-webkit-scrollbar]:w-nano',
  '[&::-webkit-scrollbar-track]:my-mini [&::-webkit-scrollbar-track]:bg-transparent',
  '[&::-webkit-scrollbar-thumb]:rounded-pill [&::-webkit-scrollbar-thumb]:bg-ctx-interactive-secondary',
  '[&::-webkit-scrollbar-thumb:hover]:bg-ctx-interactive-secondaryHover',
  '[@supports_not_selector(::-webkit-scrollbar)]:[scrollbar-width:thin]',
  '[@supports_not_selector(::-webkit-scrollbar)]:[scrollbar-color:theme(colors.ctx.interactive.secondary)_transparent]',
].join(' ');

interface MenuLinkProps extends Pick<SidebarLayoutProps, 'renderLink'> {
  item: SidebarLayoutItem;
}

const MenuLink = ({ item, renderLink }: MenuLinkProps) => {
  const content = (
    <>
      {item.icon}
      <span className='group-data-[expanded=false]:sr-only ts-subtitle-xs'>{item.label}</span>
    </>
  );

  if (renderLink) return renderLink(item, { className: menuLinkClassName, children: content });

  return (
    <LinkButton
      type='button'
      onClick={item.onClick}
      aria-current={item.active ? 'page' : undefined}
      custom={{ root: menuLinkClassName }}
      hierarchy='secondary'
    >
      {content}
    </LinkButton>
  );
};

const menuLinkClassName =
  '!flex !items-center !justify-start text-left group-data-[expanded=false]:!justify-center gap-micro p-nano w-full rounded-md !text-ctx-content-base/75 aria-[current="page"]:bg-[#D6E4FF7A] aria-[current="page"]:!text-ctx-content-button group-data-[expanded=true]:hover:bg-[#D6E4FF7A]';

interface MenuGroupProps extends Pick<SidebarLayoutProps, 'renderLink'> {
  item: SidebarLayoutItem;
}

const MenuGroup = ({ item, renderLink }: MenuGroupProps) => {
  const label = (
    <div className='flex items-center gap-micro text-ctx-content-base'>
      {item.icon}
      <span className='group-data-[expanded=false]:sr-only'>{item.label}</span>
    </div>
  );

  return (
    <>
      <div className='w-full flex items-center justify-center group-data-[expanded=true]:hidden'>
        <Dropdown
          className='!z-[60]'
          trigger={
            <Button type='button' hierarchy='tertiary' custom={{ root: '!w-full !rounded-pill hover:!bg-[#D6E4FF7A]' }}>
              {label}
            </Button>
          }
        >
          {item.children?.map((child) => (
            <DropdownItem key={child.id} asChild={!!renderLink} onClick={renderLink ? undefined : child.onClick}>
              {renderLink ? renderLink(child, { children: child.label }) : child.label}
            </DropdownItem>
          ))}
        </Dropdown>
      </div>

      <div className='w-full relative group-data-[expanded=false]:hidden'>
        <AccordionItem
          value={item.id}
          icon='right'
          className='w-full flex'
          custom={{ content: '!pl-0' }}
          label={label as unknown as string}
        >
          <div className='space-y-quark [&_a_span]:pl-sm'>
            {item.children?.map((child) => (
              <MenuLink key={child.id} item={child} renderLink={renderLink} />
            ))}
          </div>
        </AccordionItem>
      </div>
    </>
  );
};

const findActiveGroupIds = (items: SidebarLayoutItem[]): string[] =>
  items.filter((item) => item.children?.some((child) => child.active)).map((item) => item.id);

export { SidebarLayout };
